import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useSelector } from 'react-redux';
import { usePrevious } from 'hooks';
import { debounce } from 'lodash';

import VehiclesGroupsTagsStyled from './VehiclesGroupsTags.styled';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { truncateKeywordSpaces } from 'utils/utils';

const AsyncVehiclesGroupsSelect = ({
  label,
  variant,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  datacy,
  customParams,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState([]);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const { list, loadingList } = useSelector((state) => state.vehiclesGroups);

  const fetchVehiclesGroups = useCallback(
    debounce((params) => {
      if (
        params &&
        params.hasOwnProperty('keyword') &&
        (params.keyword === undefined || params.keyword === '')
      )
        delete params.keyword;
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(newParams));
    }, 1000),
    []
  );

  const prevvalue = usePrevious(value);

  useEffect(() => {
    if (!value) return;
    fetchVehiclesGroups(params);
  }, []) // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      setDefaultValue(value || []);
    }
  }, [value, list]) // eslint-disable-line

  const handleFetchVehiclesGroupTags = () => {
    if (defaultValue.length === 0 && !loadingList && !params?.keyword)
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(params));
  };

  return (
    <Autocomplete
      multiple
      openOnFocus
      options={list}
      fullwidth="true"
      value={defaultValue}
      loading={loadingList}
      id="vehicle-groups-tags-select"
      onHighlightChange={handleFetchVehiclesGroupTags}
      getOptionLabel={(option) => option.name || ''}
      onInputChange={(ev, val) => {
        fetchVehiclesGroups({ ...params, keyword: val });
      }}
      onChange={(ev, val) => {
        setFieldValue(name, val || []);
      }}
      getOptionSelected={(option, value) => {
        return option.id === value.id;
      }}
      renderInput={(params) => (
        <VehiclesGroupsTagsStyled
          {...params}
          label={label}
          datacy={datacy}
          variant={variant}
          error={errors && errors[name] && touched && touched[name]}
          helperText={errors && errors[name] && touched && touched[name] && errors[name]}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingList ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

AsyncVehiclesGroupsSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AsyncVehiclesGroupsSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  omitFields: [],
  omitFieldKey: '',
  label: 'Select...',
  variant: 'standard',
  datacy: 'vehicle-groups-select',
};

export default AsyncVehiclesGroupsSelect;
